import {
    autoUpdate,
    flip,
    offset,
    type Placement,
    shift,
    useFloating,
} from "@floating-ui/react-dom";
import { useState } from "react";
import styled, { keyframes } from "styled-components";
import Portal from "../../common/components/Portal";
import { palette, shadows, zIndex } from "../../styles/constants";
import { prefersReducedMotion } from "../../util/prefersReducedMotion";
import useInert from "../../util/useInert";
import { useSheetContext } from "../Sheet/Context";
import { Sheet } from "../Sheet/Sheet";

type MenuButtonProps = {
    children:
        | React.ReactNode
        | ((toggle: () => void, active?: boolean) => React.ReactNode);
    target: (onClick: () => void, active: boolean) => React.ReactNode;
    position?: "bottom-center" | "bottom-right" | "bottom-left" | "top-left";
    overflow?: "visible" | "auto";
    withinPortal?: boolean;
    initialOpen?: boolean;
    className?: string;
    bodyStyle?: React.CSSProperties;
    withInert?: boolean;
    listenersIgnoreModals?: boolean;
};

const positionPlacement = (
    position: MenuButtonProps["position"],
): Placement => {
    switch (position) {
        case "bottom-center":
            return "bottom";
        case "bottom-right":
            return "bottom-end";
        case "bottom-left":
            return "bottom-start";
        case "top-left":
            return "top-start";
        default:
            return "bottom";
    }
};

// Where the menu grows from and shrinks back into, the edge at the button
const transformOrigin = (placement: Placement) => {
    const [side, align] = placement.split("-");
    const y = side === "top" ? "bottom" : "top";
    const x = align === "start" ? "left" : align === "end" ? "right" : "center";
    return `${y} ${x}`;
};

const MenuButtonInner = (props: MenuButtonProps) => {
    const {
        children,
        target,
        position,
        overflow = "auto",
        withinPortal = false,
        className,
        bodyStyle,
        withInert = false,
    } = props;
    const { isSheetPresent, setSheetPresent, id } = useSheetContext();
    // Kept mounted after closing until the exit animation has played. Set
    // while rendering, not in an effect, so the body is never unmounted for
    // a render in between (the portal and position would start over, which
    // flickers).
    const [closing, setClosing] = useState(false);
    const [wasOpen, setWasOpen] = useState(isSheetPresent);
    if (wasOpen !== isSheetPresent) {
        setWasOpen(isSheetPresent);
        setClosing(!isSheetPresent && !prefersReducedMotion());
    }
    const bodyMounted = isSheetPresent || closing;

    const { refs, floatingStyles, placement } = useFloating({
        placement: positionPlacement(position),
        middleware: [offset(), flip(), shift()],
        whileElementsMounted: autoUpdate,
        open: bodyMounted,
        // Position with top/left, the open and close animations scale it
        transform: false,
    });
    const bodyProps = {
        "data-sheet": id,
        ref: refs.setFloating,
        "data-visible": bodyMounted,
        "data-closing": closing,
        "data-overflow": overflow,
        onAnimationEnd: (event: React.AnimationEvent<HTMLDivElement>) => {
            if (closing && event.target === event.currentTarget) {
                setClosing(false);
            }
        },
        className,
    };
    const originStyle = { transformOrigin: transformOrigin(placement) };

    useInert(
        refs.reference as React.RefObject<HTMLElement>,
        isSheetPresent && withInert,
    );

    const toggle = () => {
        setSheetPresent((prev) => !prev);
    };

    if (withinPortal) {
        return (
            <>
                <Wrapper ref={refs.setReference} data-sheet-trigger={id}>
                    {target(toggle, isSheetPresent)}
                    {bodyMounted && (
                        <Portal>
                            <Body
                                {...bodyProps}
                                style={{
                                    ...floatingStyles,
                                    ...originStyle,
                                    zIndex: zIndex.mount_everest,
                                    ...bodyStyle,
                                }}
                            >
                                {typeof children === "function"
                                    ? children(toggle, isSheetPresent)
                                    : children}
                            </Body>
                        </Portal>
                    )}
                </Wrapper>
            </>
        );
    }

    return (
        <>
            <Wrapper ref={refs.setReference} data-sheet-trigger={id}>
                {target(toggle, isSheetPresent)}
                {bodyMounted && (
                    <Body
                        {...bodyProps}
                        style={{ ...floatingStyles, ...originStyle }}
                    >
                        {typeof children === "function"
                            ? children(toggle)
                            : children}
                    </Body>
                )}
            </Wrapper>
        </>
    );
};

export default function MenuButton(props: MenuButtonProps) {
    return (
        <Sheet
            initialOpen={props.initialOpen}
            ignoreModals={props.listenersIgnoreModals}
        >
            <MenuButtonInner {...props} />
        </Sheet>
    );
}

const Wrapper = styled.div`
    position: relative;
`;

const menuIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
`;

const menuOut = keyframes`
  to {
    opacity: 0;
    transform: scale(0.95);
  }
`;

const Body = styled.div`
    transform-origin: top center;
    animation: ${menuIn} 0.18s cubic-bezier(0.22, 1, 0.36, 1);

    /* Doubled to win over data-visible below */
    &&[data-closing="true"] {
        animation: ${menuOut} 0.12s ease-in forwards;
        pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
    background: white;
    border-radius: 8px;
    border: 1px solid ${palette.neutral[200]};
    box-shadow: ${shadows.large};
    padding: 8px;
    z-index: ${zIndex.mount_everest};
    width: 100%;
    max-width: 300px;
    min-width: max-content;

    max-height: 900px;
    scrollbar-width: thin;
    scrollbar-color: ${palette.neutral[200]} white;

    &[data-overflow="auto"] {
        overflow: auto;
    }

    &[data-overflow="visible"] {
        overflow: visible;
    }

    &[data-visible="false"] {
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
        content-visibility: hidden;
    }
    &[data-visible="true"] {
        opacity: 1;
        pointer-events: auto;
        visibility: visible;
        content-visibility: visible;
    }

    &.maw-100 {
        max-width: 100px;
    }

    &.no-padding {
        padding: 0;
    }
`;
